import * as React from 'react'
import { ArrowRight, RefreshCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useT } from '@/components/visitone/visitone-provider'
import { ModuleShell } from '@/components/today/module-shell'
import { useTodayModule } from '@/hooks/use-today-module'
import type { NeedsAttentionItem, NeedsAttentionKind } from '@/lib/bff/today'
import type { TodayModuleProps } from '@/components/today/modules/types'

const MAX_ROWS = 5

/** Where a "Review" action routes for each problem kind (intent paths; resolved by the dashboard). */
const REVIEW_ROUTE: Record<NeedsAttentionKind, string> = {
  'failed-sends': '/guests?filter=message-failed',
  'unsynced-menu': '/setup/menus',
  'orders-not-delivered': '/insights?filter=undelivered',
  'expiring-memberships': '/guests?filter=expiring',
}

function sortBySeverityThenRecency(items: NeedsAttentionItem[]): NeedsAttentionItem[] {
  return [...items].sort(
    (a, b) =>
      b.severity - a.severity ||
      new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  )
}

export function NeedsAttentionModule({
  tenantId,
  venue,
  headingId,
  onOpenRoute,
  canOpenRoute,
  onDismiss,
  refusal,
}: TodayModuleProps) {
  const t = useT()
  const title = t('today.moduleTitles.needs-attention')
  const { data, error, isLoading, retry } = useTodayModule('needs-attention', tenantId, venue)
  const [syncing, setSyncing] = React.useState<Record<string, boolean>>({})

  // Hidden entirely when there is nothing to report (VIS-257 AC5).
  if (!isLoading && !error && (!data || data.items.length === 0)) return null

  const sorted = data ? sortBySeverityThenRecency(data.items) : []
  const visible = sorted.slice(0, MAX_ROWS)
  const overflow = sorted.length - visible.length

  const startSync = (id: string) => {
    setSyncing((prev) => ({ ...prev, [id]: true }))
    // The real BFF resolves inline; the mock refetches after a short delay.
    setTimeout(() => {
      setSyncing((prev) => ({ ...prev, [id]: false }))
      void retry()
    }, 800)
  }

  return (
    <ModuleShell
      title={title}
      headingId={headingId}
      isLoading={isLoading}
      error={error}
      onRetry={retry}
      onDismiss={onDismiss}
      refusal={refusal}
      loadingRows={4}
    >
      <ul className="flex flex-col divide-y divide-line">
        {visible.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-3 first:pt-0">
            <span className="flex min-w-0 items-start gap-2 text-sm text-fg">
              <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-fg-muted" />
              <span className="min-w-0">{t(`today.needsAttention.${item.kind}`, { count: item.count })}</span>
            </span>
            {item.resolution === 'resolve' ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={syncing[item.id]}
                onClick={() => startSync(item.id)}
              >
                <RefreshCw className={syncing[item.id] ? 'motion-safe:animate-spin' : undefined} />
                {t('today.needsAttention.sync')}
              </Button>
            ) : canOpenRoute(REVIEW_ROUTE[item.kind]) ? (
              <Button variant="secondary" size="sm" onClick={() => onOpenRoute(REVIEW_ROUTE[item.kind])}>
                {t('today.needsAttention.review')}
                <ArrowRight />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {overflow > 0 ? (
        <p className="text-sm text-fg-muted">{t('today.module.andMore', { count: overflow })}</p>
      ) : null}
    </ModuleShell>
  )
}
